import { Injectable } from '@angular/core';
import { LS } from '../services/local-storage';
import { environment } from '../../environments/environment';

@Injectable()
export class AppStore {

  static LS_KEY_EVENT = 'DS_KEY_EVENT';
  static LS_KEY_ROOM = 'DS_KEY_ROOM';
  static LS_KEY_COMPANY_TOKEN = 'DS_KEY_COMPANY_TOKEN';
  static LS_KEY_COMPANY = 'DS_KEY_COMPANY';
  static LS_KEY_APP = 'DS_KEY_APP';

  private _eventId: string;
  private _roomId: string;
  private _appId: string;
  private _companyId: string;
  private _companyToken: string;
  private _quickCode: string;
  private _autoRotationInterval = 0;
  private _posterId: string;
  private _category: string;
  private _track: string;
  private _isOnDemand = environment.posters.isOnDemand;
  private _navigationDisabled = environment.posters.navigationDisabled;
  private _isDebug = environment.posters.isDebug;
  private _baseURL = '';
  private _postersPage = '';
  private _sourceUrl = '';

  constructor() { }

  get eventId(): string {
    this._eventId = this._eventId || LS.getItem(AppStore.LS_KEY_EVENT) || '';
    return this._eventId;
  }

  set eventId(eventId: string) {
    if (eventId) {
      this.checkAndForget(eventId);
      this._eventId = eventId;
      LS.setItem(AppStore.LS_KEY_EVENT, eventId);
    }
  }

  get roomId(): string {
    this._roomId = this._roomId || LS.getItem(AppStore.LS_KEY_ROOM) || '';
    return this._roomId;
  }

  set roomId(roomId: string) {
    if (roomId) {
      this._roomId = roomId;
      LS.setItem(AppStore.LS_KEY_ROOM, roomId);
    }
  }

  flushEvent() {
    this._eventId = null;
    LS.removeItem(AppStore.LS_KEY_EVENT);
  }

  flushRoom() {
    this._roomId = null;
    LS.removeItem(AppStore.LS_KEY_ROOM);
  }

  checkAndForget(eventId) {
    if (LS.getItem(AppStore.LS_KEY_EVENT) && (eventId !== LS.getItem(AppStore.LS_KEY_EVENT))) {
      this.flushRoom();
    }
  }

  get appId(): string {
    this._appId = this._appId || LS.getItem(AppStore.LS_KEY_APP) || '';
    return this._appId;
  }

  set appId(appId: string) {
    if (appId) {
      this._appId = appId;
      LS.setItem(AppStore.LS_KEY_APP, appId);
    }
  }

  flushAppId() {
    this._appId = null;
    LS.removeItem(AppStore.LS_KEY_APP);
  }

  get companyId(): string {
    this._companyId = this._companyId || LS.getItem(AppStore.LS_KEY_COMPANY) || '';
    return this._companyId;
  }

  set companyId(companyId: string) {
    if (companyId) {
      this._companyId = companyId;
      LS.setItem(AppStore.LS_KEY_COMPANY, companyId);
    }
  }

  flushCompanyId() {
    this._companyId = null;
    LS.removeItem(AppStore.LS_KEY_COMPANY);
  }

  get companyToken(): string {
    this._companyToken = this._companyToken || LS.getItem(AppStore.LS_KEY_COMPANY_TOKEN) || '';
    return this._companyToken;
  }

  set companyToken(companyToken: string) {
    if (companyToken) {
      this._companyToken = companyToken;
      LS.setItem(AppStore.LS_KEY_COMPANY_TOKEN, companyToken);
    }
  }

  flushCompany() {
    this._companyToken = null;
    LS.removeItem(AppStore.LS_KEY_COMPANY_TOKEN);
  }

  get quickCode(): string {
    this._quickCode = this._quickCode || '';
    return this._quickCode;
  }

  set quickCode(quickCode: string) {
    if (quickCode) {
      this._quickCode = quickCode;
    }
  }

  flushQuickCode() {
    this._quickCode = null;
  }

  get autoRotationInterval(): number {
    this._autoRotationInterval = this._autoRotationInterval || 0;
    return this._autoRotationInterval;
  }

  set autoRotationInterval(autoRotationInterval: number) {
    if (autoRotationInterval) {
      this._autoRotationInterval = autoRotationInterval;
    }
  }

  flushAutoRotationInterval() {
    this._autoRotationInterval = 0;
  }

  get posterId(): string {
    this._posterId = this._posterId || '';
    return this._posterId;
  }

  set posterId(posterId: string) {
    if (posterId) {
      this._posterId = posterId;
    }
  }

  flushPosterId() {
    this._posterId = '';
  }

  get category(): string {
    this._category = this._category || 'all';
    return this._category;
  }

  set category(category: string) {
    if (category) {
      this._category = category;
    }
  }

  flushCategory() {
    this._category = 'all';
  }

  get track(): string {
    this._track = this._track || 'all';
    return this._track;
  }

  set track(track: string) {
    if (track) {
      this._track = track;
    }
  }

  flushTrack() {
    this._track = 'all';
  }

  get isOnDemand(): boolean {
    return this._isOnDemand;
  }

  set isOnDemand(isOnDemand: boolean) {
    this._isOnDemand = isOnDemand;
  }

  flushIsOnDemand() {
    this._isOnDemand = environment.posters.isOnDemand;
  }

  get navigationDisabled(): boolean {
    return this._navigationDisabled;
  }

  set navigationDisabled(navigationDisabled: boolean) {
    this._navigationDisabled = navigationDisabled;
  }

  flushNavigationDisabled() {
    this._navigationDisabled = environment.posters.navigationDisabled;
  }

  get isDebug(): boolean {
    return this._isDebug;
  }

  set isDebug(isDebug: boolean) {
    this._isDebug = isDebug;
  }

  flushIsDebug() {
    this._isDebug = environment.posters.isDebug;
  }

  get baseURL(): string {
    this._baseURL = this._baseURL
                    || environment.posters.baseURL + environment.posters.prefix + environment.posters.basePath;
    return this._baseURL;
  }

  set baseURL(baseURL: string) {
    if (baseURL) {
      this._baseURL = baseURL;
    }
  }

  flushBaseURL() {
    this._baseURL = '';
  }

  get postersPage(): string {
    this._postersPage = this._postersPage
                        || this._baseURL + environment.posters.postersPagePath;
    return this._postersPage;
  }

  set postersPage(postersPage: string) {
    if (postersPage) {
      this._postersPage = postersPage;
    }
  }

  flushPostersPage() {
    this._postersPage = '';
  }

  get sourceUrl(): string {
    this._sourceUrl = this._sourceUrl || this._baseURL + environment.posters.sourceEndpoint;
    return this._sourceUrl;
  }

  set sourceUrl(sourceUrl: string) {
    if (sourceUrl) {
      this._sourceUrl = sourceUrl;
    }
  }

  flushSourceUrl() {
    this._sourceUrl = '';
  }
}
